import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { guardedLocalStorage } from "../../../utils/persistentStorage";
import { GROUP_BY_OPTIONS } from "../constants";
import { GroupBy } from "./types";

export type TerminalActivityFilterStatus = "open" | "archive" | "unclear";

interface TerminalManagementState {
  searchTerm: string;
  filterStatuses: Record<TerminalActivityFilterStatus, boolean>;
  groupBy: string;
  isAllGroupsExpanded: boolean;
  collapseAllSignal: boolean;
  pins: Record<string, boolean>;
  selectedTerminalId: string | null;
  minTerminalDate: Date | null;
  maxTerminalDate: Date | null;
  deletePin: (id: string) => void;
  togglePin: (id: string) => void;
  toggleAllGroupsExpanded: () => void;
  setIsAllGroupsExpanded: (value: boolean) => void;
  setSelectedTerminalId: (terminalId: string | null) => void;
  setMinTerminalDate: (date: Date | null) => void;
  setMaxTerminalDate: (date: Date | null) => void;
  setGroupBy: (groupBy: string | null) => void;
  toggleFilterStatus: (status: TerminalActivityFilterStatus) => void;
  setSearchTerm: (term: string) => void;
}

export const useTerminalManagementStore = create<TerminalManagementState>()(
  persist(
    set => ({
      pins: {},
      isAllGroupsExpanded: false,
      collapseAllSignal: false,
      filterStatuses: { open: true, archive: false, unclear: false },
      groupBy: "noGroup",
      selectedTerminalId: null,
      minTerminalDate: new Date(),
      maxTerminalDate: new Date(),
      searchTerm: "",
      deletePin: (id: string) =>
        set(state => {
          const updatedPins = { ...state.pins };
          delete updatedPins[id];
          return { pins: updatedPins };
        }),
      togglePin: (id: string) =>
        set(state => ({
          pins: {
            ...state.pins,
            [id]: !state.pins[id],
          },
        })),
      toggleAllGroupsExpanded: () =>
        set(state => ({
          isAllGroupsExpanded: !state.isAllGroupsExpanded,
          collapseAllSignal: state.isAllGroupsExpanded
            ? !state.collapseAllSignal
            : state.collapseAllSignal,
        })),
      setIsAllGroupsExpanded: (value: boolean) =>
        set(() => ({ isAllGroupsExpanded: value })),
      setSelectedTerminalId: (terminalId: string | null) =>
        set(() => ({
          selectedTerminalId: terminalId,
        })),
      setMinTerminalDate: (date: Date | null) =>
        set(() => ({
          minTerminalDate: date,
        })),
      setMaxTerminalDate: (date: Date | null) =>
        set(() => ({
          maxTerminalDate: date,
        })),
      setGroupBy: (groupBy: string | null) =>
        set(() => ({
          groupBy: groupBy ? groupBy : "noGroup",
        })),
      toggleFilterStatus: (status: TerminalActivityFilterStatus) =>
        set(state => ({
          filterStatuses: {
            ...state.filterStatuses,
            [status]: !state.filterStatuses[status],
          },
        })),
      setSearchTerm: (searchTerm: string) =>
        set(() => ({
          searchTerm,
        })),
    }),
    {
      name: "TerminalManagementStore",
      storage: createJSONStorage(() => guardedLocalStorage),
      version: 5,
      partialize: state => ({
        searchTerm: state.searchTerm,
        isAllGroupsExpanded: state.isAllGroupsExpanded,
        pins: state.pins,
        groupBy: state.groupBy,
        filterStatuses: state.filterStatuses,
        selectedTerminalId: state.selectedTerminalId,
        minTerminalDate: state.minTerminalDate,
        maxTerminalDate: state.maxTerminalDate,
      }),
    }
  )
);

const isValidGroupBy = (value: string): value is GroupBy => {
  return GROUP_BY_OPTIONS.includes(value as GroupBy);
};

export const groupBySelector = (state: TerminalManagementState): GroupBy =>
  isValidGroupBy(state.groupBy) ? state.groupBy : "noGroup";
